import { Key } from "../../libs/Key.js";
import { input } from '../input/InputManager.js';
import { createGameSeed, gameRandomState, seedGameRandom } from './GameRandom.js';
import { advanceSimulationClock, resetSimulationClock, simulationFrame } from './SimulationClock.js';
import { onFrameBudgetCutoff, setReplayCutoffs } from './FrameBudget.js';

// Records everything the simulation reads from the player, per simulated frame,
// so a run replays identically on any machine. A run is every attempt since the
// level timer started: retries from a checkpoint continue it, a restart begins a
// new one. Once a second a hash of all bodies is stored too; a replay compares it
// to report the first frame where it went out of sync.
const REPLAY_VERSION = 2;
const HASH_INTERVAL = 60;
const NO_KEYS = [];
export const REPLAY_SPEEDS = [0.25, 0.5, 1, 2, 4, 8];

let run = null;
let attempt = null;
let runBefore = null;
let lastReplay = null;
let session = null;

let recordedActions = NO_KEYS;
let recordedKeys = NO_KEYS;
let recordedMouseX = NaN;
let recordedMouseY = NaN;
let recordedScaleX = NaN;
let recordedScaleY = NaN;
let recordedPointerLock = false;

onFrameBudgetCutoff((queue, examined) => {
	attempt?.events.push([simulationFrame(), 'b', [queue, examined]]);
});

const f32 = new Float32Array(1);
const u32 = new Uint32Array(f32.buffer);
const mix = (hash, value) => {
	f32[0] = value;
	return Math.imul(hash ^ u32[0], 16777619) >>> 0;
};
const hashWorld = world => {
	let hash = Math.imul(0x811c9dc5 ^ gameRandomState(), 16777619) >>> 0;
	for(let body = world.GetBodyList(); Box2D.getPointer(body) !== 0; body = body.GetNext()){
		const position = body.GetPosition();
		hash = mix(hash, position.get_x());
		hash = mix(hash, position.get_y());
		hash = mix(hash, body.GetAngle());
	}
	return hash;
};

const sameActions = (actions, set) => {
	if(actions.length !== set.size) return false;
	for(const action of actions) if(!set.has(action)) return false;
	return true;
};
const sameKeys = (keys, object) => {
	let count = 0;
	for(const key in object){
		if(keys[count] !== key) return false;
		count++;
	}
	return count === keys.length;
};
const hasKeys = object => {
	for(const _key in object) return true;
	return false;
};
const keyObject = keys => {
	const object = {};
	for(const key of keys) object[key] = true;
	return object;
};

const recordFrame = (frame, game) => {
	const events = attempt.events;
	if(!sameActions(recordedActions, input.down)){
		recordedActions = [...input.down];
		events.push([frame, 'a', recordedActions]);
	}
	if(input.pressed.size) events.push([frame, 'ap', [...input.pressed]]);
	if(input.released.size) events.push([frame, 'ar', [...input.released]]);
	if(!sameKeys(recordedKeys, Key._down)){
		recordedKeys = Object.keys(Key._down);
		events.push([frame, 'k', recordedKeys]);
	}
	if(hasKeys(Key._pressed)) events.push([frame, 'kp', Object.keys(Key._pressed)]);
	if(hasKeys(Key._released)) events.push([frame, 'kr', Object.keys(Key._released)]);
	// The mouse position only matters to the simulation when a button changes.
	if(Key._down[Key.MOUSE] || Key._pressed[Key.MOUSE] || Key._released[Key.MOUSE]){
		const mouse = game.editor.mousePosWorld;
		const mouseX = mouse.get_x();
		const mouseY = mouse.get_y();
		if(mouseX !== recordedMouseX || mouseY !== recordedMouseY){
			recordedMouseX = mouseX;
			recordedMouseY = mouseY;
			events.push([frame, 'm', [mouseX, mouseY]]);
		}
	}
	// Raw screen pixels and the zoom stay short in JSON; the division repeats exactly.
	const pointer = input.pendingPointer;
	if(pointer.x || pointer.y || pointer.moved){
		const scale = game.editor.cameraHolder.scale;
		if(scale.x !== recordedScaleX || scale.y !== recordedScaleY){
			recordedScaleX = scale.x;
			recordedScaleY = scale.y;
			events.push([frame, 'z', [scale.x, scale.y]]);
		}
		events.push([frame, 'p', [pointer.x, pointer.y, pointer.moved ? 1 : 0]]);
	}
	if(input.pointerLocked !== recordedPointerLock){
		recordedPointerLock = input.pointerLocked;
		events.push([frame, 'l', recordedPointerLock ? 1 : 0]);
	}
	attempt.frames = frame;
};

const applyFrame = (frame, game) => {
	const events = session.attempt.events;
	let actionsPressed = NO_KEYS, actionsReleased = NO_KEYS, keysPressed = NO_KEYS, keysReleased = NO_KEYS;
	let pointer = null;
	if(session.coastRelease){
		({actions: actionsReleased, keys: keysReleased} = session.coastRelease);
		session.coastRelease = null;
	}
	const cutoffs = {};
	while(session.cursor < events.length && events[session.cursor][0] === frame){
		const [, type, value] = events[session.cursor++];
		switch(type){
			case 'a': session.actions = value; break;
			case 'ap': actionsPressed = value; break;
			case 'ar': actionsReleased = value; break;
			case 'k': session.keys = value; break;
			case 'kp': keysPressed = value; break;
			case 'kr': keysReleased = value; break;
			case 'm': session.mouse.Set(value[0], value[1]); break;
			case 'p': pointer = value; break;
			case 'z': session.scale = value; break;
			case 'l': session.pointerLocked = value === 1; break;
			case 'b': cutoffs[value[0]] = value[1]; break;
		}
	}
	// Live events mutate these between frames, so every frame gets fresh copies.
	input.down = new Set(session.actions);
	input.pressed = new Set(actionsPressed);
	input.released = new Set(actionsReleased);
	Key._down = keyObject(session.keys);
	Key._pressed = keyObject(keysPressed);
	Key._released = keyObject(keysReleased);
	game.editor.mousePosWorld = session.mouse;
	if(pointer) input.applyPointerMovement(pointer[0], pointer[1], pointer[2] === 1, session.scale[0], session.scale[1]);
	else input.applyPointerMovement(0, 0, false, 1, 1);
	input.pointerLocked = session.pointerLocked;
	setReplayCutoffs(cutoffs);
};

const checkHash = (frame, hash) => {
	if(!session){
		attempt.hashes.push([frame, hash]);
		return;
	}
	const hashes = session.attempt.hashes;
	while(session.hashCursor < hashes.length && hashes[session.hashCursor][0] < frame) session.hashCursor++;
	const expected = hashes[session.hashCursor];
	if(!expected || expected[0] !== frame) return;
	if(expected[1] === hash) return;
	if(!session.mismatch) {
		session.mismatch = {attempt: session.index, frame};
		console.warn(`Replay went out of sync in attempt ${session.index + 1} between frame ${frame - HASH_INTERVAL} and ${frame}`);
	}
};

const finishSession = completed => {
	const {replay, onEnd} = session;
	session = null;
	setReplayCutoffs(null);
	onEnd?.(completed, replay);
};

export const isReplaying = () => session !== null;
export const replayStatus = () => session && {
	replay: session.replay,
	attempt: session.index,
	frame: session.playing ? simulationFrame() : session.attempt.frames,
	attemptFrames: session.attempt.frames,
	speed: session.speed,
	paused: session.paused,
	ended: session.ended,
	mismatch: session.mismatch,
};

export const getLastReplay = () => lastReplay;

export const getCurrentReplay = () => run;

// Called first in playWorld: starts recording, or plays the next replay attempt.
export const beginAttempt = game => {
	resetSimulationClock();
	input.clearPointerMovement();
	recordedActions = recordedKeys = NO_KEYS;
	recordedMouseX = recordedMouseY = recordedScaleX = recordedScaleY = NaN;
	recordedPointerLock = false;
	if(session){
		const replayAttempt = session.replay.attempts[session.index];
		Object.assign(session, {
			attempt: replayAttempt, pending: false, playing: true, cursor: 0, hashCursor: 0,
			actions: NO_KEYS, keys: NO_KEYS, scale: [1, 1], pointerLocked: false,
		});
		seedGameRandom(replayAttempt.seed);
		attempt = null;
		return;
	}
	const seed = createGameSeed();
	seedGameRandom(seed);
	const player = game.replayPlayerName?.();
	if(!player){
		attempt = run = runBefore = null;
		return;
	}
	const level = game.currentLevelData;
	attempt = {seed, checkpoint: null, frames: 0, events: [], hashes: []};
	runBefore = run;
	run = {
		version: REPLAY_VERSION,
		game: typeof __VERSION__ === 'string' ? __VERSION__ : null,
		player,
		level: level ? {id: level.id, md5: level.level_md5, title: level.title, json: level.json} : null,
		character: game.selectedCharacter,
		vehicle: game.selectedVehicle,
		mask: game.selectedMask,
		attempts: [attempt],
	};
};

export const recordCheckpoint = checkpoint => {
	if(!attempt) return;
	const {x, y, rotation, flipped, persistentTriggers, frame, id} = checkpoint;
	attempt.checkpoint = {x, y, rotation, flipped, persistentTriggers: [...persistentTriggers], frame, id};
	if(runBefore?.level && runBefore.level.id === run.level?.id){
		runBefore.attempts.push(attempt);
		runBefore.finished = false;
		run = runBefore;
	}
	runBefore = null;
};

export const recordFinish = () => {
	if(run) run.finished = true;
};

// After a retry the level timer restarts when the loading screen hides, a few
// frames into the attempt.
export const recordTimerStart = gameFrame => {
	if(attempt) attempt.timer = [simulationFrame(), gameFrame];
};

export const endAttempt = () => {
	if(attempt){
		if(!attempt.frames) run.attempts.pop();
		if(run.attempts.length) lastReplay = run;
		attempt = null;
	}
	if(session && !session.pending) finishSession(false);
};

export const updatesThisFrame = () => {
	if(!session) return 1;
	if(session.paused || session.ended){
		const steps = session.steps;
		session.steps = 0;
		return steps;
	}
	session.accumulator += session.speed;
	const updates = Math.floor(session.accumulator);
	session.accumulator -= updates;
	return updates;
};

// Called once per simulated frame, after the live input was read. Returns false
// when the frame must not be simulated: a replay attempt ran out of frames.
export const beginFrame = game => {
	if(session){
		if(!session.playing) return false;
		const timer = session.attempt.timer;
		if(timer && simulationFrame() === timer[0]) session.onTimerStart?.(timer[1], session.attempt);
		const past = simulationFrame() - session.attempt.frames;
		if(past >= 0){
			const last = session.index + 1 >= session.replay.attempts.length;
			if(!last || !session.runOn?.(past)){
				session.playing = false;
				if(!last) session.onAttemptEnd?.();
				else session.ended = true;
				return false;
			}
			// Past the recording the level runs on with every input released.
			if(past === 0){
				session.coastRelease = {actions: session.actions, keys: session.keys};
				session.actions = session.keys = NO_KEYS;
				session.pointerLocked = false;
			}
		}
	}
	advanceSimulationClock();
	const frame = simulationFrame();
	if(frame % HASH_INTERVAL === 0 && (attempt || session)) checkHash(frame, hashWorld(game.world));
	if(session){
		applyFrame(frame, game);
		return true;
	}
	const pointer = input.pendingPointer;
	const scale = game.editor.cameraHolder.scale;
	input.applyPointerMovement(pointer.x, pointer.y, pointer.moved, scale.x, scale.y);
	input.pointerLocked = document.pointerLockElement === game.canvas;
	if(attempt) recordFrame(frame, game);
	input.clearPointerMovement();
	return true;
};

// The replay starts with the next playWorld. onAttemptEnd() asks for the next
// attempt to be built, after which nextAttempt() and playWorld continue it.
// onTimerStart(gameFrame, attempt) repeats a level timer restart,
// runOn(frames) keeps simulating that many frames past the end of the last
// attempt while it returns true, and onEnd(completed, replay) runs when the
// replay is stopped.
export const queueReplay = (replay, {onAttemptEnd, onTimerStart, runOn, onEnd} = {}) => {
	if(session) finishSession(false);
	session = {
		replay, onAttemptEnd, onTimerStart, runOn, onEnd, index: 0, pending: true, playing: false, ended: false,
		attempt: replay.attempts[0], mismatch: null, mouse: new Box2D.b2Vec2(0, 0),
		speed: 1, paused: false, steps: 0, accumulator: 0,
	};
};

export const nextAttempt = () => {
	session.index++;
	session.pending = true;
	return session.replay.attempts[session.index];
};

export const stopReplay = () => {
	if(session) finishSession(session.ended);
};

export const setReplaySpeed = speed => {
	if(session) session.speed = speed;
};

export const setReplayPaused = paused => {
	if(session) session.paused = paused;
};

export const stepReplay = () => {
	if(session?.paused && session.playing) session.steps++;
};

const toBase64 = bytes => {
	let binary = '';
	for(let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
	return btoa(binary);
};

// The run so far, for the server: it has the level, so only its revision is
// kept. Serialized right away, so later frames of this attempt are not included.
export const encodeReplayForUpload = (replay = run) => {
	if(!replay?.level) return null;
	const {level, ...rest} = replay;
	const json = JSON.stringify({...rest, level: {id: level.id, md5: level.md5}});
	// Plain JSON goes through the blackbox, which only encrypts single-byte characters.
	if(typeof CompressionStream === 'undefined') {
		return Promise.resolve(json.replace(/[^\x00-\x7f]/g, c => `\\u${c.charCodeAt(0).toString(16).padStart(4, '0')}`));
	}
	const gzip = new Blob([json]).stream().pipeThrough(new CompressionStream('gzip'));
	return new Response(gzip).arrayBuffer().then(buffer => toBase64(new Uint8Array(buffer)));
};

// Replay files on the static server are gzip, or JSON from browsers without
// CompressionStream.
export const decodeReplay = async buffer => {
	const bytes = new Uint8Array(buffer);
	const json = bytes[0] === 0x1f && bytes[1] === 0x8b
		? await new Response(new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'))).text()
		: new TextDecoder().decode(bytes);
	return parseReplay(json);
};

export const parseReplay = data => {
	const replay = typeof data === 'string' ? JSON.parse(data) : data;
	if(replay?.version !== REPLAY_VERSION) throw new Error(`Unsupported replay version ${replay?.version}`);
	if(replay.level?.id === undefined || !replay.attempts?.length) throw new Error('Replay has no level or attempts');
	if(typeof __VERSION__ === 'string' && replay.game && replay.game !== __VERSION__){
		console.warn(`Replay was recorded with JollyWorld ${replay.game} and may not play back identically`);
	}
	return replay;
};
